import { motion } from "framer-motion";
import {
  ShieldCheck,
  Headphones,
  Wrench,
  GraduationCap,
  MonitorSmartphone,
  BadgeIndianRupee,
} from "lucide-react";
import { fadeUp, stagger } from "@/lib/motion";

const CARDS = [
  {
    Icon: ShieldCheck,
    title: "Certified Experts",
    desc: "Tally-certified engineers with deep understanding of Indian tax & accounting workflows.",
    highlight: false,
  },
  {
    Icon: Headphones,
    title: "Fast Support",
    desc: "Priority phone, email & WhatsApp support with strict 2-hour response SLAs.",
    highlight: true,
  },
  {
    Icon: Wrench,
    title: "Installation & Setup",
    desc: "On-site and remote installation, license activation and multi-user configuration.",
    highlight: false,
  },
  {
    Icon: GraduationCap,
    title: "Training",
    desc: "Hands-on classroom & remote training for finance teams, accountants and owners.",
    highlight: false,
  },
  {
    Icon: MonitorSmartphone,
    title: "Remote Assistance",
    desc: "Secure remote sessions to fix issues, migrate data and configure add-ons.",
    highlight: false,
  },
  {
    Icon: BadgeIndianRupee,
    title: "Affordable Pricing",
    desc: "Transparent Tally licensing, AMC and rental plans — with best-in-market bundles.",
    highlight: false,
  },
];

export default function WhyChooseUs() {
  return (
    <section id="why-choose-us" data-testid="why-section" className="relative py-24 lg:py-32 bg-[#F5F7FA]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          variants={stagger(0.1, 0.08)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.3 }}
          className="max-w-2xl"
        >
          <motion.span variants={fadeUp} className="eyebrow"><span className="dot" /> Why Choose Us</motion.span>
          <motion.h2
            variants={fadeUp}
            className="mt-6 font-display font-black text-4xl sm:text-5xl leading-[1.08] tracking-tight text-[#1A1A2E]"
          >
            How Professional Tally Services{" "}
            <span className="font-serif-accent text-[#FFA000]">Can Drive Success.</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="mt-5 text-[17px] text-[#4A5568] max-w-xl leading-relaxed">
            From licensing to lifetime support — we make TallyPrime effortless for every business, big or small.
          </motion.p>
        </motion.div>

        <motion.div
          variants={stagger(0.06, 0.08)}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, amount: 0.2 }}
          className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5"
        >
          {CARDS.map(({ Icon, title, desc, highlight }, i) => (
            <motion.div
              key={title}
              variants={fadeUp}
              whileHover={{ y: -6 }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
              className={`group relative rounded-2xl p-7 transition-all duration-500 overflow-hidden ${
                highlight
                  ? "bg-[#FFA000] text-white shadow-[0_20px_50px_rgba(255,160,0,0.3)]"
                  : "bg-white border border-[#0D47A1]/8 shadow-[0_8px_30px_rgba(13,71,161,0.05)] hover:shadow-[0_25px_50px_rgba(13,71,161,0.1)]"
              }`}
              data-testid={`why-card-${i}`}
            >
              {/* Corner glow */}
              <div className={`absolute -top-16 -right-16 w-40 h-40 rounded-full blur-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
                highlight ? "bg-white/20" : "bg-[#FFA000]/10"
              }`} />

              <div className="relative">
                <div className={`w-14 h-14 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-105 transition-transform duration-500 ${
                  highlight ? "bg-white/20" : "bg-[#0D47A1]/8"
                }`} style={!highlight ? { backgroundColor: "rgba(13,71,161,0.08)" } : {}}>
                  <Icon className={`w-6 h-6 ${highlight ? "text-white" : "text-[#0D47A1]"}`} strokeWidth={1.75} />
                </div>
                <h3 className={`font-display font-bold text-xl tracking-tight ${highlight ? "text-white" : "text-[#1A1A2E]"}`}>{title}</h3>
                <p className={`mt-2 text-[14.5px] leading-relaxed ${highlight ? "text-white/90" : "text-[#4A5568]"}`}>{desc}</p>
              </div>

              <div className={`absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 ${
                highlight ? "via-white/50" : "via-[#FFA000]/40"
              }`} />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
